import { useEffect, useRef, useCallback, useState } from 'react';
import { useGameStore } from '@hollowveil/shared';

export function MessageBox() {
  const { currentMessage, clearMessage } = useGameStore();
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Reset timer when message changes
  const startTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      if (!isPaused) {
        clearMessage();
      }
    }, 6000); // 6 seconds instead of 4
  }, [clearMessage, isPaused]);

  useEffect(() => {
    if (currentMessage) {
      setIsPaused(false);
      startTimer();
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [currentMessage, startTimer]);

  // Restart timer when unpaused
  useEffect(() => {
    if (!isPaused && currentMessage) {
      startTimer();
    }
  }, [isPaused, currentMessage, startTimer]);

  // Pause timer on scroll or touch
  const handleInteractionStart = useCallback(() => {
    setIsPaused(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, []);

  // Resume timer after interaction ends (with delay)
  const handleInteractionEnd = useCallback(() => {
    // Give user time to finish reading after scrolling
    setTimeout(() => {
      setIsPaused(false);
    }, 3000);
  }, []);

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    clearMessage();
  }, [clearMessage]);

  if (!currentMessage) {
    return null;
  }

  return (
    <div className="message-box-container">
      <button
        className="message-box-close"
        onClick={handleClose}
        aria-label="Fermer"
      >
        ✕
      </button>
      <div
        ref={contentRef}
        className="message-box-content"
        onScroll={handleInteractionStart}
        onTouchStart={handleInteractionStart}
        onTouchEnd={handleInteractionEnd}
        onMouseDown={handleInteractionStart}
        onMouseUp={handleInteractionEnd}
      >
        {currentMessage}
      </div>
    </div>
  );
}
